import React, { useState, useEffect } from 'react';
import { Card, Button, Input, Select, CustomDropdown } from '../ui';
import treatmentRecordService from '../../services/TreatmentRecordService';
import WorkOrderService from '../../services/WorkOrderService';
import { TREATMENT_RECORD_STATUS } from '../../models/TreatmentRecord';
import { WORK_ORDER_STATUS } from '../../models/WorkOrder';
import treatmentService from '../../services/TreatmentService';
import venueService from '../../services/VenueService';
import practitionerService from '../../services/PractitionerService';
import appointmentService from '../../services/AppointmentService';

const TABS = [
  { key: 'health', label: '健康评估' },
  { key: 'tcm', label: '四诊信息' },
  { key: 'plan', label: '调理方案' },
  { key: 'service', label: '服务实施' },
  { key: 'follow', label: '随访与转诊' },
];

const TreatmentRecordTabsEditor = ({ record, onSave }) => {
  const [activeTabIdx, setActiveTabIdx] = useState(0);
  const [editRecord, setEditRecord] = useState(record);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(false);
  const [history, setHistory] = useState([]);
  const [formEnabled, setFormEnabled] = useState(record.status !== TREATMENT_RECORD_STATUS.DRAFT);
  const [treatmentOptions, setTreatmentOptions] = useState([]);
  const [treatmentType, setTreatmentType] = useState('basic');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [projectNote, setProjectNote] = useState('');
  const [venueOptions, setVenueOptions] = useState([]);
  const [practitionerOptions, setPractitionerOptions] = useState([]);

  const activeTab = TABS[activeTabIdx];

  // 获取历史调理记录
  useEffect(() => {
    if (record.customerId) {
      const all = treatmentRecordService.getTreatmentRecordsByCustomer(record.customerId) || [];
      // 排除当前记录，按时间倒序
      setHistory(all.filter(r => r.id !== record.id).sort((a, b) => new Date(b.date) - new Date(a.date)));
    }
  }, [record.customerId, record.id]);

  useEffect(() => {
    // 获取全部调理项目
    const treatments = treatmentService.getAllTreatments();
    setTreatmentOptions(treatments.map(t => ({ value: t.id, label: t.name, type: t.type })));
    // 获取房间
    setVenueOptions(venueService.getAllVenues().map(v => ({ value: v.id, label: v.name })));
    // 获取调理师
    setPractitionerOptions(practitionerService.getAllPractitioners().map(p => ({ value: p.id, label: p.name })));
  }, []);

  // 获取分组
  const basicProjects = treatmentOptions.filter(opt => opt.type === 'basic');
  const specialProjects = treatmentOptions.filter(opt => opt.type === 'project');

  // 添加项目
  const handleAddProject = () => {
    if (!selectedProjectId) return;
    const selected = treatmentOptions.find(opt => opt.value === selectedProjectId);
    if (!selected) return;
    const newItem = { projectId: selected.value, name: selected.label, type: selected.type, note: projectNote };
    const items = Array.isArray(editRecord?.treatmentPlan?.items) ? [...editRecord.treatmentPlan.items] : [];
    // 避免重复
    if (items.some(i => i.projectId === newItem.projectId)) return;
    handleFieldChange('plan', 'items', [...items, newItem]);
    setSelectedProjectId('');
    setProjectNote('');
  };

  // 删除项目
  const handleRemoveProject = (projectId) => {
    const items = Array.isArray(editRecord?.treatmentPlan?.items) ? [...editRecord.treatmentPlan.items] : [];
    handleFieldChange('plan', 'items', items.filter(i => i.projectId !== projectId));
  };

  // 编辑备注
  const handleEditNote = (projectId, note) => {
    const items = Array.isArray(editRecord?.treatmentPlan?.items) ? [...editRecord.treatmentPlan.items] : [];
    handleFieldChange('plan', 'items', items.map(i => i.projectId === projectId ? { ...i, note } : i));
  };

  // 开始调理
  const handleStart = async () => {
    setStarting(true);
    try {
      let updatedWorkOrder = null;
      // 更新工单状态
      if (editRecord.workOrderId) {
        WorkOrderService.updateWorkOrder(editRecord.workOrderId, { status: WORK_ORDER_STATUS.IN_PROGRESS });
        updatedWorkOrder = WorkOrderService.getWorkOrderById(editRecord.workOrderId);
      }
      // 更新调理记录状态
      const updated = treatmentRecordService.updateTreatmentRecord(editRecord.id, { status: TREATMENT_RECORD_STATUS.SUBMITTED });
      setEditRecord(updated);
      setFormEnabled(true);
      if (onSave) onSave(updated, updatedWorkOrder);
    } catch (e) {
      setError('启动失败');
    } finally {
      setStarting(false);
    }
  };

  // 字段变更
  const handleFieldChange = (tab, field, value, subfield) => {
    setEditRecord(prev => {
      const updated = { ...prev };
      if (tab === 'health') {
        if (subfield) {
          updated.healthProfile = {
            ...updated.healthProfile,
            [field]: {
              ...updated.healthProfile[field],
              [subfield]: value
            }
          };
        } else {
          updated.healthProfile = {
            ...updated.healthProfile,
            [field]: value
          };
        }
      } else if (tab === 'tcm') {
        updated.tcmAssessment = {
          ...updated.tcmAssessment,
          [field]: value
        };
      } else if (tab === 'plan') {
        updated.treatmentPlan = {
          ...updated.treatmentPlan,
          [field]: value
        };
      } else if (tab === 'service') {
        updated.serviceExecution = {
          ...updated.serviceExecution,
          [field]: value
        };
      } else if (tab === 'follow') {
        updated.followUp = value;
      }
      return updated;
    });
  };

  // 分tab保存
  const handleTabSave = async () => {
    setSaving(true);
    setError(null);
    try {
      let updateData = {};
      if (activeTab.key === 'health') {
        updateData = { healthProfile: editRecord.healthProfile };
      } else if (activeTab.key === 'tcm') {
        updateData = { tcmAssessment: editRecord.tcmAssessment };
      } else if (activeTab.key === 'plan') {
        updateData = { treatmentPlan: editRecord.treatmentPlan };
      } else if (activeTab.key === 'service') {
        updateData = { serviceExecution: editRecord.serviceExecution };
      } else if (activeTab.key === 'follow') {
        updateData = { followUp: editRecord.followUp };
      }
      // 判断是否为最后一个tab
      let updatedRecord = null;
      let updatedWorkOrder = null;
      if (activeTabIdx === TABS.length - 1) {
        // 完成：归档调理记录，完成工单，结算金额
        const newSettlement = {
          ...(editRecord.settlement || {}),
          totalAmount: 200,
          paidAmount: 0
        };
        updatedRecord = treatmentRecordService.updateTreatmentRecord(editRecord.id, { ...updateData, status: TREATMENT_RECORD_STATUS.ARCHIVED, settlement: newSettlement });
        if (editRecord.workOrderId) {
          WorkOrderService.updateWorkOrder(editRecord.workOrderId, { status: WORK_ORDER_STATUS.COMPLETED });
          updatedWorkOrder = WorkOrderService.getWorkOrderById(editRecord.workOrderId);
        }
        // 若有 appointmentId，自动设置预约为已完成
        if (editRecord.appointmentId) {
          appointmentService.updateAppointmentStatus(editRecord.appointmentId, 'completed');
        }
      } else {
        updatedRecord = treatmentRecordService.updateTreatmentRecord(editRecord.id, updateData);
      }
      setEditRecord(updatedRecord);
      if (onSave) onSave(updatedRecord, updatedWorkOrder);
      // 自动切换到下一个tab
      if (activeTabIdx < TABS.length - 1) setActiveTabIdx(activeTabIdx + 1);
    } catch (e) {
      setError('保存失败');
    } finally {
      setSaving(false);
    }
  };

  // 上一步
  const handlePrev = () => {
    if (activeTabIdx > 0) setActiveTabIdx(activeTabIdx - 1);
  };

  return (
    <Card className="mb-6">
      {/* 未开始时只显示按钮和历史 */}
      {editRecord.status === TREATMENT_RECORD_STATUS.DRAFT ? (
        <div className="p-8 flex flex-col items-center">
          <Button onClick={handleStart} loading={starting} disabled={starting} className="mb-8">
            开始调理
          </Button>
          {history.length > 0 && (
            <div className="w-full max-w-xl p-4 bg-gray-50 rounded-xl border border-gray-100">
              <div className="font-semibold mb-2 text-emerald-700">历史调理记录</div>
              <ul className="space-y-1 text-sm text-gray-700">
                {history.slice(0, 3).map(h => (
                  <li key={h.id}>
                    <span>{new Date(h.date).toLocaleDateString()}：</span>
                    <span>{h.treatmentProjectName || '无项目'}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <>
          {/* 顶部分步按钮+连接线 */}
          <div className="mb-4">
            <div className="flex justify-between items-center relative z-20" style={{paddingLeft: '2.5rem', paddingRight: '2.5rem'}}>
              {TABS.map((tab, idx) => (
                <React.Fragment key={tab.key}>
                  <button
                    className={`px-3 py-1 rounded border text-sm font-medium transition-all duration-200 whitespace-nowrap
                      ${activeTabIdx === idx
                        ? 'bg-emerald-500 text-white border-emerald-500 shadow'
                        : idx < activeTabIdx
                          ? 'bg-white text-emerald-500 border-emerald-500'
                          : 'bg-white text-gray-300 border-gray-300'}
                    `}
                    style={{minWidth: 90, maxWidth: 140}}
                    onClick={() => setActiveTabIdx(idx)}
                  >
                    {tab.label}
                  </button>
                  {/* 连接线，最后一个tab不显示 */}
                  {idx < TABS.length - 1 && (
                    <div
                      className={`flex-1 h-1 mx-1 rounded-full transition-all duration-200
                        ${idx < activeTabIdx ? 'bg-emerald-400' : 'bg-gray-200'}`}
                      style={{ minWidth: 24, maxWidth: 48 }}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
          {/* 表单内容直接渲染，无需再嵌套Card */}
          <div className="p-6 min-h-[300px] relative">
            {error && <div className="mb-4 text-red-500 text-sm">{error}</div>}
            {/* 健康评估 */}
            {activeTab.key === 'health' && (
              <div className="space-y-4">
                <div className="flex space-x-4">
                  <Input label="身高(cm)" type="number" value={editRecord?.healthProfile?.height || ''} onChange={e => handleFieldChange('health', 'height', e.target.value)} />
                  <Input label="体重(kg)" type="number" value={editRecord?.healthProfile?.weight || ''} onChange={e => handleFieldChange('health', 'weight', e.target.value)} />
                </div>
                <Input label="家族史" value={editRecord?.healthProfile?.familyHistory || ''} onChange={e => handleFieldChange('health', 'familyHistory', e.target.value)} placeholder="如：高血压、糖尿病、心脏病等" />
                <Input label="饮食习惯" value={editRecord?.healthProfile?.lifestyle?.diet || ''} onChange={e => handleFieldChange('health', 'lifestyle', e.target.value, 'diet')} placeholder="如：清淡、荤素搭配、偏咸、喜欢辛辣等" />
                <Input label="作息习惯" value={editRecord?.healthProfile?.lifestyle?.sleep || ''} onChange={e => handleFieldChange('health', 'lifestyle', e.target.value, 'sleep')} placeholder="如：早睡早起、熬夜、午休等" />
                <Input label="运动习惯" value={editRecord?.healthProfile?.lifestyle?.exercise || ''} onChange={e => handleFieldChange('health', 'lifestyle', e.target.value, 'exercise')} placeholder="如：每周3次跑步、偶尔锻炼、久坐少动等" />
                <Input label="其他生活习惯" value={editRecord?.healthProfile?.lifestyle?.other || ''} onChange={e => handleFieldChange('health', 'lifestyle', e.target.value, 'other')} placeholder="如：吸烟、饮酒、喝咖啡等" />
                <Input label="其他" value={editRecord?.healthProfile?.other || ''} onChange={e => handleFieldChange('health', 'other', e.target.value)} />
              </div>
            )}
            {/* 四诊信息 */}
            {activeTab.key === 'tcm' && (
              <div className="space-y-6">
                {/* 望诊 */}
                <div>
                  <div className="font-semibold text-emerald-700 mb-2">望诊</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input label="舌象照片" value={editRecord?.tcmAssessment?.inspection?.tongueImage || ''} onChange={e => handleFieldChange('tcm', 'inspection', { ...editRecord?.tcmAssessment?.inspection, tongueImage: e.target.value })} placeholder="请上传或粘贴舌象照片链接" />
                    <Input label="面部照片" value={editRecord?.tcmAssessment?.inspection?.faceImage || ''} onChange={e => handleFieldChange('tcm', 'inspection', { ...editRecord?.tcmAssessment?.inspection, faceImage: e.target.value })} placeholder="请上传或粘贴面部照片链接" />
                  </div>
                  <Input label="舌象描述" value={editRecord?.tcmAssessment?.inspection?.description || ''} onChange={e => handleFieldChange('tcm', 'inspection', { ...editRecord?.tcmAssessment?.inspection, description: e.target.value })} placeholder="如：淡红舌，薄白苔" />
                </div>
                {/* 闻诊 */}
                <div>
                  <div className="font-semibold text-emerald-700 mb-2">闻诊</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input label="声音" value={editRecord?.tcmAssessment?.listeningSmelling?.voice || ''} onChange={e => handleFieldChange('tcm', 'listeningSmelling', { ...editRecord?.tcmAssessment?.listeningSmelling, voice: e.target.value })} placeholder="如：声音低弱/高亢" />
                    <Input label="气味" value={editRecord?.tcmAssessment?.listeningSmelling?.smell || ''} onChange={e => handleFieldChange('tcm', 'listeningSmelling', { ...editRecord?.tcmAssessment?.listeningSmelling, smell: e.target.value })} placeholder="如：口臭/无异常" />
                  </div>
                </div>
                {/* 问诊 */}
                <div>
                  <div className="font-semibold text-emerald-700 mb-2">问诊</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <textarea className="border rounded-xl p-3 w-full min-h-[48px]" placeholder="主诉（如：反复头痛3天）" value={editRecord?.tcmAssessment?.inquiry?.chiefComplaint || ''} onChange={e => handleFieldChange('tcm', 'inquiry', { ...editRecord?.tcmAssessment?.inquiry, chiefComplaint: e.target.value })} />
                    <textarea className="border rounded-xl p-3 w-full min-h-[48px]" placeholder="现病史（如：近一周感冒后出现头痛...）" value={editRecord?.tcmAssessment?.inquiry?.history || ''} onChange={e => handleFieldChange('tcm', 'inquiry', { ...editRecord?.tcmAssessment?.inquiry, history: e.target.value })} />
                  </div>
                  <Input label="症状（逗号分隔）" value={editRecord?.tcmAssessment?.inquiry?.symptoms?.join(',') || ''} onChange={e => handleFieldChange('tcm', 'inquiry', { ...editRecord?.tcmAssessment?.inquiry, symptoms: e.target.value.split(',') })} placeholder="如：头痛、恶心、乏力" />
                </div>
                {/* 切诊 */}
                <div>
                  <div className="font-semibold text-emerald-700 mb-2">切诊</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input label="脉象" value={editRecord?.tcmAssessment?.palpation?.pulse || ''} onChange={e => handleFieldChange('tcm', 'palpation', { ...editRecord?.tcmAssessment?.palpation, pulse: e.target.value })} placeholder="如：弦脉、滑脉" />
                    <Input label="脉值/其他" value={editRecord?.tcmAssessment?.palpation?.pulseValue || ''} onChange={e => handleFieldChange('tcm', 'palpation', { ...editRecord?.tcmAssessment?.palpation, pulseValue: e.target.value })} placeholder="如：脉搏72次/分" />
                  </div>
                  <Input label="其他脉诊" value={editRecord?.tcmAssessment?.palpation?.other || ''} onChange={e => handleFieldChange('tcm', 'palpation', { ...editRecord?.tcmAssessment?.palpation, other: e.target.value })} placeholder="如：细数脉" />
                </div>
                {/* 体质辨识 */}
                <div className="mb-8">
                  <div className="font-semibold text-emerald-700 mb-3">体质辨识</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col">
                      <label className="mb-1 text-gray-700 font-medium">体质类型</label>
                      <select className="border rounded-xl h-[48px] text-base px-4 w-full" value={editRecord?.tcmAssessment?.constitution?.type || ''} onChange={e => handleFieldChange('tcm', 'constitution', { ...editRecord?.tcmAssessment?.constitution, type: e.target.value })} >
                        <option value="">请选择体质类型</option>
                        <option value="平和">平和</option>
                        <option value="气虚">气虚</option>
                        <option value="阳虚">阳虚</option>
                        <option value="阴虚">阴虚</option>
                        <option value="痰湿">痰湿</option>
                        <option value="湿热">湿热</option>
                        <option value="血瘀">血瘀</option>
                        <option value="气郁">气郁</option>
                        <option value="特禀">特禀</option>
                      </select>
                    </div>
                    <Input label="体质分数" value={editRecord?.tcmAssessment?.constitution?.score || ''} onChange={e => handleFieldChange('tcm', 'constitution', { ...editRecord?.tcmAssessment?.constitution, score: e.target.value })} placeholder="如：85" />
                  </div>
                </div>
                {/* 风险分析 */}
                <div>
                  <div className="font-semibold text-emerald-700 mb-2">风险分析</div>
                  <textarea className="border rounded-xl p-3 w-full min-h-[48px]" placeholder="如：有高血压家族史，需注意..." value={editRecord?.tcmAssessment?.riskAnalysis || ''} onChange={e => handleFieldChange('tcm', 'riskAnalysis', e.target.value)} />
                </div>
              </div>
            )}
            {/* 调理方案 */}
            {activeTab.key === 'plan' && (
              <div className="space-y-4">
                {/* 调理项目分组添加 */}
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <div className="flex flex-col md:flex-row md:items-end gap-4">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">项目类型</label>
                      <select className="border rounded-xl px-4 py-3 w-full text-base" value={treatmentType} onChange={e => { setTreatmentType(e.target.value); setSelectedProjectId(''); }}>
                        <option value="basic">基础项目</option>
                        <option value="project">特色项目</option>
                      </select>
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">调理项目</label>
                      <select className="border rounded-xl px-4 py-3 w-full text-base" value={selectedProjectId} onChange={e => setSelectedProjectId(e.target.value)}>
                        <option value="">请选择项目</option>
                        {(treatmentType === 'basic' ? basicProjects : specialProjects).map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">备注</label>
                      <input className="border rounded-xl px-4 py-3 w-full text-base" placeholder="如：加强推拿力度" value={projectNote} onChange={e => setProjectNote(e.target.value)} />
                    </div>
                    <div>
                      <Button size="medium" onClick={handleAddProject} disabled={!selectedProjectId}>添加</Button>
                    </div>
                  </div>
                  {/* 已添加项目列表 */}
                  {Array.isArray(editRecord?.treatmentPlan?.items) && editRecord.treatmentPlan.items.length > 0 && (
                    <div className="mt-4">
                      <div className="font-semibold text-gray-700 mb-2">已添加项目</div>
                      <ul className="space-y-2">
                        {editRecord.treatmentPlan.items.map(item => (
                          <li key={item.projectId} className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border">
                            <span className="flex-1 font-medium text-gray-900">{item.name} <span className="text-xs text-gray-400 ml-2">[{item.type === 'basic' ? '基础' : '特色'}]</span></span>
                            <input className="border rounded px-2 py-1 text-sm w-48" placeholder="备注" value={item.note || ''} onChange={e => handleEditNote(item.projectId, e.target.value)} />
                            <Button size="small" variant="danger" onClick={() => handleRemoveProject(item.projectId)}>删除</Button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                {/* 处方多行输入 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">处方</label>
                  <textarea
                    className="border rounded-xl px-4 py-3 w-full min-h-[64px] text-base"
                    placeholder="每行填写一种药物及用法，如：黄芪10g，水煎服"
                    value={(editRecord?.treatmentPlan?.prescriptions || []).join('\n')}
                    onChange={e => handleFieldChange('plan', 'prescriptions', e.target.value.split(/\r?\n/).filter(line => line.trim() !== ''))}
                  />
                </div>
                <Input label="饮食建议（逗号分隔）" value={editRecord?.treatmentPlan?.dietAdvice?.join(',') || ''} onChange={e => handleFieldChange('plan', 'dietAdvice', e.target.value.split(','))} placeholder="如：多吃蔬菜,少油腻" />
                <Input label="运动建议（逗号分隔）" value={editRecord?.treatmentPlan?.exerciseAdvice?.join(',') || ''} onChange={e => handleFieldChange('plan', 'exerciseAdvice', e.target.value.split(','))} placeholder="如：每日快走30分钟" />
                <Input label="生活建议（逗号分隔）" value={editRecord?.treatmentPlan?.lifestyleAdvice?.join(',') || ''} onChange={e => handleFieldChange('plan', 'lifestyleAdvice', e.target.value.split(','))} placeholder="如：早睡早起,保持心情愉快" />
                <Input label="方案备注" value={editRecord?.treatmentPlan?.planNotes || ''} onChange={e => handleFieldChange('plan', 'planNotes', e.target.value)} placeholder="如：本方案适合气虚体质" />
                {/* 打印按钮 */}
                <div className="flex justify-end pt-2">
                  <Button variant="secondary" onClick={() => window.alert('打印成功，演示系统，实际打印功能待接入')}>打印方案</Button>
                </div>
              </div>
            )}
            {/* 服务实施 */}
            {activeTab.key === 'service' && (
              <div className="space-y-6">
                {/* 注意事项（过敏）分组 */}
                <div className="p-4 bg-yellow-50 rounded-xl border border-yellow-200">
                  <div className="font-semibold text-yellow-700 mb-2">注意事项（请先询问客户）</div>
                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editRecord?.serviceExecution?.sopChecklist?.alcoholAllergy} onChange={e => handleFieldChange('service', 'sopChecklist', { ...editRecord?.serviceExecution?.sopChecklist, alcoholAllergy: e.target.checked })} />酒精过敏</label>
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editRecord?.serviceExecution?.sopChecklist?.essentialOilAllergy} onChange={e => handleFieldChange('service', 'sopChecklist', { ...editRecord?.serviceExecution?.sopChecklist, essentialOilAllergy: e.target.checked })} />精油过敏</label>
                  </div>
                </div>
                {/* 本次调理项目分组 */}
                <div className="p-4 bg-gray-50 rounded-xl border border-emerald-200">
                  <div className="flex flex-col md:flex-row gap-4 mb-2">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">房间</label>
                      <select className="border rounded-xl px-4 py-3 w-full text-base" value={editRecord?.serviceExecution?.schedule?.room || ''} onChange={e => handleFieldChange('service', 'schedule', { ...editRecord?.serviceExecution?.schedule, room: e.target.value })}>
                        <option value="">请选择房间</option>
                        {venueOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                      </select>
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">操作人</label>
                      <select className="border rounded-xl px-4 py-3 w-full text-base" value={editRecord?.serviceExecution?.schedule?.operatorId || ''} onChange={e => handleFieldChange('service', 'schedule', { ...editRecord?.serviceExecution?.schedule, operatorId: e.target.value })}>
                        <option value="">请选择调理师</option>
                        {practitionerOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                      </select>
                    </div>
                  </div>
                  {Array.isArray(editRecord?.treatmentPlan?.items) && editRecord.treatmentPlan.items.length > 0 && (
                    <>
                      <div className="font-semibold text-emerald-700 mb-2">本次调理项目</div>
                      <div className="flex gap-6 mb-2 text-sm text-gray-600">
                        <div>整体开始时间：{(() => {
                          const steps = Array.isArray(editRecord?.serviceExecution?.steps) ? editRecord.serviceExecution.steps : [];
                          const first = steps.find(s => s.startTime);
                          return first?.startTime ? new Date(first.startTime).toLocaleString() : '未记录';
                        })()}</div>
                        <div>整体结束时间：{(() => {
                          const steps = Array.isArray(editRecord?.serviceExecution?.steps) ? editRecord.serviceExecution.steps : [];
                          const last = [...steps].reverse().find(s => s.endTime);
                          return last?.endTime ? new Date(last.endTime).toLocaleString() : '未记录';
                        })()}</div>
                      </div>
                      <ul className="space-y-2">
                        {editRecord.treatmentPlan.items.map((item, idx) => {
                          const steps = Array.isArray(editRecord?.serviceExecution?.steps) ? editRecord.serviceExecution.steps : [];
                          const step = steps[idx] || {};
                          const start = step.startTime || '';
                          const end = step.endTime || '';
                          let duration = '';
                          if (start && end) {
                            const s = new Date(start), e = new Date(end);
                            duration = Math.round((e - s) / 60000);
                          }
                          const nowStr = () => new Date().toISOString().slice(0,16);
                          return (
                            <li key={item.projectId} className="flex flex-col md:flex-row md:items-center gap-2 bg-white rounded-lg px-3 py-2 border">
                              <div className="flex-1 font-medium text-gray-900">{item.name} <span className="text-xs text-gray-500 ml-2">[{item.type === 'basic' ? '基础' : '特色'}]</span>{item.note && <span className="text-xs text-emerald-700 ml-2">备注：{item.note}</span>}</div>
                              <div className="flex items-center gap-2">
                                <label className="text-xs text-gray-500">开始</label>
                                {start ? <span className="text-xs text-gray-700">{new Date(start).toLocaleString()}</span> : <Button size="small" onClick={() => {
                                  const steps = Array.isArray(editRecord?.serviceExecution?.steps) ? [...editRecord.serviceExecution.steps] : [];
                                  steps[idx] = { ...steps[idx], startTime: nowStr() };
                                  handleFieldChange('service', 'steps', steps);
                                }}>记录</Button>}
                                <label className="text-xs text-gray-500">完成</label>
                                {end ? <span className="text-xs text-gray-700">{new Date(end).toLocaleString()}</span> : <Button size="small" onClick={() => {
                                  const steps = Array.isArray(editRecord?.serviceExecution?.steps) ? [...editRecord.serviceExecution.steps] : [];
                                  steps[idx] = { ...steps[idx], endTime: nowStr() };
                                  handleFieldChange('service', 'steps', steps);
                                }}>记录</Button>}
                                {duration && <span className="text-xs text-gray-700 ml-2">用时：{duration}分钟</span>}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </>
                  )}
                </div>
                {/* 实时反馈分组 */}
                <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                  <div className="font-semibold text-green-700 mb-2">实时反馈</div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">实时反馈-疼痛等级 <span className="text-xs text-gray-400 ml-2">（0=无，10=剧烈）</span></label>
                    <input type="range" min="0" max="10" step="1" value={editRecord?.serviceExecution?.realTimeFeedback?.painLevel || 0} onChange={e => handleFieldChange('service', 'realTimeFeedback', { ...editRecord?.serviceExecution?.realTimeFeedback, painLevel: e.target.value })} className="w-full" />
                    <div className="flex justify-between text-xs text-gray-400 px-1"><span>无</span><span>轻</span><span>中</span><span>重</span><span>剧烈</span></div>
                  </div>
                  <Input label="实时反馈-热区" value={editRecord?.serviceExecution?.realTimeFeedback?.heatArea || ''} onChange={e => handleFieldChange('service', 'realTimeFeedback', { ...editRecord?.serviceExecution?.realTimeFeedback, heatArea: e.target.value })} placeholder="如：背部、腰部发热" />
                  <Input label="实时反馈-家属反馈" value={editRecord?.serviceExecution?.realTimeFeedback?.parentFeedback || ''} onChange={e => handleFieldChange('service', 'realTimeFeedback', { ...editRecord?.serviceExecution?.realTimeFeedback, parentFeedback: e.target.value })} placeholder="如：家属反馈良好" />
                </div>
                {/* 后续护理分组 */}
                <div className="p-4 bg-yellow-50 rounded-xl border border-yellow-200">
                  <div className="font-semibold text-yellow-700 mb-2">后续护理</div>
                  <Input label="后续护理（逗号分隔）" value={editRecord?.serviceExecution?.aftercare?.join(',') || ''} onChange={e => handleFieldChange('service', 'aftercare', e.target.value.split(','))} placeholder="如：多休息，避免受凉" />
                  <Input label="服务备注" value={editRecord?.serviceExecution?.serviceNotes || ''} onChange={e => handleFieldChange('service', 'serviceNotes', e.target.value)} placeholder="如：本次调理顺利完成" />
                </div>
              </div>
            )}
            {/* 随访与转诊 */}
            {activeTab.key === 'follow' && (
              <div className="space-y-4">
                {/* 随访内容 多行输入 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">随访内容</label>
                  <textarea
                    className="border rounded-xl px-4 py-3 w-full min-h-[64px] text-base"
                    placeholder="如：患者反馈症状缓解，建议继续观察..."
                    value={editRecord?.followUp?.[0]?.content || ''}
                    onChange={e => {
                      const arr = Array.isArray(editRecord?.followUp) ? [...editRecord.followUp] : [{}];
                      arr[0] = { ...arr[0], content: e.target.value };
                      handleFieldChange('follow', 'followUp', arr);
                    }}
                  />
                </div>
                {/* 下次服务医师提示 多行输入 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">下次服务医师提示</label>
                  <textarea
                    className="border rounded-xl px-4 py-3 w-full min-h-[64px] text-base"
                    placeholder="如：下次关注血压变化，注意饮食控制..."
                    value={editRecord?.followUp?.[0]?.nextServiceNote || ''}
                    onChange={e => {
                      const arr = Array.isArray(editRecord?.followUp) ? [...editRecord.followUp] : [{}];
                      arr[0] = { ...arr[0], nextServiceNote: e.target.value };
                      handleFieldChange('follow', 'followUp', arr);
                    }}
                  />
                </div>
                {/* 转诊建议 禁用 */}
                <Input label="转诊建议" value={editRecord?.followUp?.[0]?.referralAdvice || ''} onChange={() => {}} disabled placeholder="如：建议转内科进一步检查" />
                {/* 转诊科室 禁用 */}
                <Input label="转诊科室" value={editRecord?.followUp?.[0]?.referralDepartment || ''} onChange={() => {}} disabled placeholder="如：内科/心血管科" />
              </div>
            )}
            <div className="mt-6 flex justify-between">
              <Button onClick={handlePrev} disabled={activeTabIdx === 0 || !formEnabled} variant="secondary">
                上一步
              </Button>
              <Button onClick={handleTabSave} loading={saving} disabled={saving || !formEnabled}>
                {activeTabIdx === TABS.length - 1 ? '完成' : '保存并下一步'}
              </Button>
            </div>
            {/* 历史调理记录关键信息 */}
            {history.length > 0 && (
              <div className="mt-8 p-4 bg-gray-50 rounded-xl border border-gray-100">
                <div className="font-semibold mb-2 text-emerald-700">历史调理记录</div>
                <ul className="space-y-1 text-sm text-gray-700">
                  {history.slice(0, 3).map(h => (
                    <li key={h.id}>
                      <span>{new Date(h.date).toLocaleDateString()}：</span>
                      <span>项目：{h.treatmentProjectName || '无'}，</span>
                      <span>效果：{h.treatmentPlan?.planNotes || '无'}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </>
      )}
    </Card>
  );
};

export default TreatmentRecordTabsEditor; 